import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Badge, Pagination, Popover, Table, Whisper } from "rsuite";
import { BooleanToggleFilterIcon } from "../../components/booleanToggleFilterIcon.component";
import { ModalPaymentInformation } from "../../components/modal";
import { PaginationEntriesInput } from "../../components/paginationEntries";
import { SendMail } from "../../components/sendMail.component";
import { DateCell, TextCell } from "../../components/tableItem.component";
import { InvoiceModal } from "../../components/viewInvoice.component";
import {
  ApiSettings,
  FinanceCacheIds,
  MutationReference,
  getFinances,
} from "../../core/apis";
import { StorageAuth } from "../../core/localStorage";
import {
  ActionsFinancePatientTable,
  useAppDispatch,
  useAppSelector,
} from "../../core/redux";
import { ApiSchemaFinancePatientDetails } from "../../models/api/finance";
import { sortTableData } from "../../utils/tableFilter";
import { UtilityDate } from "../../utils/dateUtils";
import { convertToTwoDecimal } from "../../utils/convertToTwoDecimal";

const { Column, HeaderCell, Cell } = Table;

type TableData = ApiSchemaFinancePatientDetails & {
  name?: string;
};

type TypeTableKey = keyof TableData;

type PatientListProps = {
  isValidating?: boolean;
  data?: ApiSchemaFinancePatientDetails[];
  refetchFinanceData: Function;
  totalCount: number;
};

const BadgeCell = ({ rowData, ...props }: { rowData?: TableData }) => {
  const status = rowData?.paymentStatus ? "Paid" : "Due";
  const getColor = (status: string) => {
    return status === "Paid"
      ? "#00BDB4"
      : status === "Due"
      ? "#FF0000"
      : "#808080";
  };

  return (
    <Cell {...props}>
      <Badge
        content={status || "Paid"}
        style={{ background: getColor(status) }}
        className="p-1 px-2"
      />
    </Cell>
  );
};

export const PatientFinanceList = ({
  data,
  isValidating,
  totalCount,
}: PatientListProps) => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const { pageNumber, pageSize, isLoading } = useAppSelector(
    (state) => state.financePatientTable
  );
  const [modalResetKey, setModalResetKey] = useState(Date.now() + "");
  const dispatch = useAppDispatch();
  const [sortBy, setSortBy] = useState<TypeTableKey>("" as TypeTableKey);
  const [sortAsc, setSortAsc] = useState(true);
  const [currentInvoiceKey, setCurrentInvoiceKey] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [filterStatus, toggleFilterStatus] = useReducer(
    (curr: number) => (curr + 1) % 3,
    2
  );
  const [haveEmail, setHaveEmail] = useState(false);
  const [isLoadingEmailData, setIsLoadingEmailData] = useState(false);

  const getEmailData = useCallback(async () => {
    setIsLoadingEmailData(true);
    const response = await ApiSettings.GetSenderEmail();
    if (response.isSuccess && response.data?.senderEmail) {
      setHaveEmail(true);
    }
    setIsLoadingEmailData(false);
  }, []);

  useEffect(() => {
    setTableData(
      data?.map((patient) => {
        const {totalAmount ,...rest} = patient
        return ({
        name: patient.patientFirstName + " " + patient.patientLastName,
        totalAmount: convertToTwoDecimal(totalAmount as number),
        ...rest,
      })}) ?? []
    );
    setModalResetKey(Date.now() + "");
  }, [data, pageNumber, pageSize]);

  const MarkAsPaidCell = ({
    rowData,
    ...props
  }: {
    rowData?: ApiSchemaFinancePatientDetails;
  }) => {
    return (
      <Cell {...props}>
        {rowData?.paymentStatus ? (
          ""
        ) : (
          <p
            className="text-green-200 border d-inline px-2 rounded-pill border-green-200 click"
            onClick={() => {
              setCurrentInvoiceKey(rowData?.invoiceSkey || "");
              setShowPaymentModal(true);
            }}
          >
            Mark as paid
          </p>
        )}
      </Cell>
    );
  };

  const filteredTableData = useMemo(() => {
    const statusFiltered =
      filterStatus === 2
        ? tableData
        : tableData.filter((td) => +td.paymentStatus === filterStatus);
    return sortTableData(statusFiltered, sortBy, sortAsc);
  }, [filterStatus, sortAsc, sortBy, tableData]);

  const handleMailSent = () => {
    getFinances(StorageAuth.CompanyId!).then((res) => {
      MutationReference.GetReference()?.(
        FinanceCacheIds.PatientDetails,
        res.data?.data,
        false
      );
    });
  };

  const getPrefilledSubject = (rowData: ApiSchemaFinancePatientDetails) => {
    return `Invoice - ${rowData.patientFirstName || ''} ${rowData.patientLastName || ''}`;
  };

  const getPrefilledMessage = (selectedDate: Date) => {
    const month = selectedDate.toLocaleString('default', { month: 'long' });
    return `Please see ${month} Invoice in this attachment`;
  };

  useEffect(() => {
    getEmailData();
  }, [getEmailData]);

  return (
    <div>
      <ModalPaymentInformation
        show={showPaymentModal}
        setShow={setShowPaymentModal}
        invoiceSKey={currentInvoiceKey}
        Action={ActionsFinancePatientTable}
        paymentFor="patient"
      />
      <div className="p-2 d-flex align-item-center">
        <div className="align-self-center me-2">Show</div>
        <PaginationEntriesInput
          onChange={(value) =>
            dispatch(ActionsFinancePatientTable.setEntryCount(value as number))
          }
        />
        <div className="align-self-center ms-2">entries</div>
      </div>

      <Table
        autoHeight
        data={filteredTableData}
        loading={isValidating || isLoading || isLoadingEmailData}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Data Available</h3>
          </div>
        )}
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortBy, sortAsc) => {
          setSortBy(sortBy as keyof TableData);
          setSortAsc(sortAsc === "asc");
        }}
        rowClassName="table-row-hover"
        bordered
        cellBordered
        hover
        classPrefix="rs-table"
        className="table-striped"
      >
        
        <Column flexGrow={1} minWidth={200} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Patient">Patient</span>
          </HeaderCell>
          <TextCell dataKey="name" />
        </Column>

        <Column flexGrow={0.6} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="From ">From </span>
          </HeaderCell>
          <Cell dataKey="from">
            {(rowData: ApiSchemaFinancePatientDetails) =>
              rowData.from ? UtilityDate.formatHumanFriendlyDate(new Date(rowData.from)) : ""
            }
          </Cell>
        </Column>

        <Column flexGrow={0.6} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="To ">To </span>
          </HeaderCell>
          <Cell dataKey="to">
            {(rowData: ApiSchemaFinancePatientDetails) =>
              rowData.to ? UtilityDate.formatHumanFriendlyDate(new Date(rowData.to)) : ""
            }
          </Cell>
        </Column>

        <Column flexGrow={0.6} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Total Amount">Total Amount</span>
          </HeaderCell>
          <TextCell component="Patient" dataKey="totalAmount" />
        </Column>

        <Column flexGrow={1} minWidth={150}>
          <HeaderCell className="fw-bold text-black">Check Number</HeaderCell>
          <TextCell dataKey="checkNumber" />
        </Column>

        <Column flexGrow={1} minWidth={150} sortable>
          <HeaderCell className="fw-bold text-black">Payment Date</HeaderCell>
          <Cell dataKey="paymentDate">
            {(rowData: ApiSchemaFinancePatientDetails) =>
              rowData.paymentDate && rowData.paymentStatus
                ? UtilityDate.formatHumanFriendlyDate(new Date(rowData.paymentDate))
                : ""
            }
          </Cell>
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">Payment Note</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaFinancePatientDetails) =>
              rowData.paymentNote ? (
                <Whisper
                  placement="topStart"
                  speaker={<Popover>{rowData.paymentNote}</Popover>}
                >
                  <span>{rowData.paymentNote}</span>
                </Whisper>
              ) : (
                ""
              )
            }
          </Cell>
        </Column>

        <Column flexGrow={0.5}>
          <HeaderCell className="fw-bold text-black">
            <span
              title="Status"
              className="click"
              onClick={() => toggleFilterStatus()}
            >
              Status
              <BooleanToggleFilterIcon
                filterValue={
                  filterStatus === 0
                    ? "true"
                    : filterStatus === 1
                    ? "false"
                    : "default"
                }
                className="ms-2 toggle-filter-icon"
              />
            </span>
          </HeaderCell>
          <BadgeCell />
        </Column>

        <Column flexGrow={0.8} >
          <HeaderCell className="fw-bold text-black">Stage</HeaderCell>
          <MarkAsPaidCell />
        </Column>

        <Column align="center" flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Action">Action</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaFinancePatientDetails) => (
              <div className="d-flex justify-content-around">
                <InvoiceModal
                  invoiceId={rowData.invoiceSkey}
                  resetKey={modalResetKey}
                  invoiceStartDate={rowData.from}
                  invoiceEndDate={rowData.to}
                  pageTitle="patient-finance"
                />
                <SendMail
                  alreadySent={rowData.isInvoiceEmailSent}
                  value={rowData}
                  fromTab="patientFinance"
                  invoiceId={rowData.invoiceSkey}
                  handleSent={handleMailSent}
                  defaultEmail={rowData.caseWorkerEmail || ""}
                  ccEmail={rowData.caseManagerEmail || ""}
                  invoiceStartDate={rowData.from}
                  invoiceEndDate={rowData.to}
                  haveEmail={haveEmail}
                  defaultSubject={getPrefilledSubject(rowData)}
                  messageGenerator={getPrefilledMessage}
                />
              </div>
            )}
          </Cell>
        </Column>
      </Table>

      {data && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            total={filteredTableData.length}
            pages={
              totalCount
                ? Math.ceil(totalCount / pageSize)
                : Math.ceil(filteredTableData.length / pageSize)
            }
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page) =>
              dispatch(ActionsFinancePatientTable.goToPage(page))
            }
          />
        </div>
      )}
    </div>
  );
};
